// ============================================
// Due Deck Builder (pure, testable)
// ============================================
//
// Builds the queue of cards to surface in a study session, in priority
// order: due cards first (sorted by nextReview ascending), then up to N
// new cards as filler. The queue is computed *at session start and on
// level change* — NOT re-derived on every wordProgress write — so a card
// maturing mid-session won't appear until the next session start.
//
// This pure function is wired into a cached signal in src/state/signals.ts.
// SRS uses absolute timestamps (Date.now()) and is timezone-agnostic; the
// time-travel test suite locks that contract.

import type { Word, WordProgress, WordProgressMap } from '@/types';

export interface BuildDueDeckOptions {
  newCardsPerSession: number;
  deckSize: number;
}

const DEFAULT_NEW_PROGRESS: Pick<WordProgress, 'state' | 'nextReview'> = {
  state: 'new',
  nextReview: 0
};

/**
 * Build the study session deck.
 *
 * @param words   the full vocabulary for the active level
 * @param progress the per-word SRS progress map (may be missing entries
 *                 for newly added words mid-deploy — vocab-drift defensive
 *                 lookup uses DEFAULT_NEW_PROGRESS as a fallback)
 * @param now     current time in ms (injected for testability)
 * @param opts    queue composition limits
 * @returns       up to opts.deckSize words: due first (by nextReview asc),
 *                then up to opts.newCardsPerSession new cards as filler
 */
export function buildDueDeck(
  words: Word[],
  progress: WordProgressMap,
  now: number,
  opts: BuildDueDeckOptions
): Word[] {
  if (words.length === 0) return [];

  const due: Array<{ word: Word; nextReview: number }> = [];
  const newCards: Word[] = [];

  for (const word of words) {
    // Vocab-drift defensive lookup: a word added in a deploy that landed
    // mid-session has no progress entry yet. Treat as new.
    const p = progress[word.japanese] ?? DEFAULT_NEW_PROGRESS;
    if (p.state === 'new') {
      newCards.push(word);
    } else if (p.nextReview <= now) {
      due.push({ word, nextReview: p.nextReview });
    }
    // Otherwise: not due yet, not new — skip.
  }

  // Due cards: sort by earliest nextReview first.
  due.sort((a, b) => a.nextReview - b.nextReview);
  const dueWords = due.map(d => d.word);

  // Cap new cards.
  const newSlice = newCards.slice(0, opts.newCardsPerSession);

  // Concatenate and cap to deckSize.
  return [...dueWords, ...newSlice].slice(0, opts.deckSize);
}
